import { useState } from 'react';
import { Link } from 'react-router';
import Logo from '@/components/Logo';
import { useEnregistrerEntete, useSoumettre, type Order } from '@/lib/orders';
import { formatMoney } from '@/lib/utils';

/**
 * Plan §5.5 — récapitulatif, PO client, notes, puis soumission.
 * Une fois soumise, la même vue devient l'écran de confirmation portant le
 * numéro de commande : le rep peut le lire au client sans changer d'écran.
 */
export default function Confirmation({ order, onRetour }: { order: Order; onRetour: () => void }) {
  const [poNumber, setPoNumber] = useState(order.poNumber ?? '');
  const [notes, setNotes] = useState(order.notes ?? '');
  const entete = useEnregistrerEntete(order.id);
  const soumettre = useSoumettre(order.id);

  const soumise = order.status === 'SUBMITTED';

  async function envoyer() {
    // L'entête est enregistrée AVANT la soumission : après, la commande est
    // figée et n'accepte plus de modification.
    await entete.mutateAsync({ poNumber: poNumber.trim() || null, notes: notes.trim() || null });
    await soumettre.mutateAsync();
  }

  return (
    <div className="mx-auto min-h-full max-w-4xl space-y-6 p-6">
      <header className="flex items-center gap-3">
        <Logo className="h-10" />
        <h1 className="text-2xl font-bold">{soumise ? 'Commande soumise' : 'Réviser la commande'}</h1>
      </header>

      {soumise && (
        <div className="rounded-xl border border-green-600/40 bg-green-600/10 p-6 text-center">
          <p className="text-sm font-medium text-green-800 dark:text-green-300">
            Commande enregistrée
          </p>
          {/* Le numéro en très gros : c'est ce que le rep lit au client. */}
          <p className="mt-1 font-mono text-4xl font-bold tracking-tight">{order.number}</p>
        </div>
      )}

      <section className="bg-card rounded-xl border p-5">
        <h2 className="text-muted-foreground text-sm font-medium">Client</h2>
        <p className="text-lg font-semibold">{order.customer.name}</p>
        <p className="text-muted-foreground font-mono text-sm">{order.customer.custNum}</p>
        {order.customer.city && (
          <p className="text-muted-foreground text-sm">
            {[order.customer.address, order.customer.city, order.customer.province]
              .filter(Boolean)
              .join(', ')}
          </p>
        )}
      </section>

      <section className="bg-card overflow-hidden rounded-xl border">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr className="text-left">
              <th className="p-3 font-medium">Pièce</th>
              <th className="p-3 font-medium">Description</th>
              <th className="p-3 text-right font-medium">Qté</th>
              <th className="p-3 text-right font-medium">Prix</th>
              <th className="p-3 text-right font-medium">Total</th>
            </tr>
          </thead>
          <tbody>
            {order.lines.map((l) => (
              <tr key={l.id} className="border-t align-top">
                <td className="p-3 font-mono text-xs">
                  <span className="text-muted-foreground">{l.lineCode}</span> {l.partNumber}
                </td>
                <td className="p-3">
                  {l.description}
                  {l.promoLabel && (
                    <span className="block text-xs font-medium text-green-700 dark:text-green-400">
                      {l.promoLabel} · −{formatMoney(l.discountAmount)}
                    </span>
                  )}
                </td>
                <td className="p-3 text-right tabular-nums">{l.qty}</td>
                <td className="p-3 text-right tabular-nums">{formatMoney(l.unitPrice)}</td>
                <td className="p-3 text-right font-medium tabular-nums">{formatMoney(l.lineTotal)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="bg-card space-y-2 rounded-xl border p-5">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Sous-total</span>
          <span className="tabular-nums">{formatMoney(order.subtotal)}</span>
        </div>
        {order.discountTotal !== '0.00' && (
          <div className="flex justify-between text-sm text-green-700 dark:text-green-400">
            <span>Rabais</span>
            <span className="tabular-nums">−{formatMoney(order.discountTotal)}</span>
          </div>
        )}
        <div className="flex items-baseline justify-between border-t pt-2">
          <span className="font-semibold">Total avant taxes</span>
          <span className="text-3xl font-bold tabular-nums">{formatMoney(order.total)}</span>
        </div>
      </section>

      {!soumise ? (
        <>
          <section className="bg-card space-y-4 rounded-xl border p-5">
            <label className="block space-y-1.5">
              <span className="text-sm font-medium">Bon de commande du client (facultatif)</span>
              <input
                value={poNumber}
                onChange={(e) => setPoNumber(e.target.value)}
                maxLength={64}
                className="border-input bg-background h-14 w-full rounded-lg border px-3 text-base"
              />
            </label>
            <label className="block space-y-1.5">
              <span className="text-sm font-medium">Notes (facultatif)</span>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                maxLength={2000}
                className="border-input bg-background w-full rounded-lg border p-3 text-base"
              />
            </label>
          </section>

          {(soumettre.isError || entete.isError) && (
            <p className="text-destructive font-medium">
              {((soumettre.error ?? entete.error) as Error).message}
            </p>
          )}

          <div className="flex flex-wrap gap-3">
            <button
              onClick={onRetour}
              className="border-input h-16 rounded-lg border px-6 text-lg font-medium"
            >
              ← Continuer les achats
            </button>
            <button
              onClick={() => void envoyer()}
              disabled={soumettre.isPending || entete.isPending || order.lines.length === 0}
              className="bg-primary text-primary-foreground h-16 flex-1 rounded-lg px-8 text-lg font-semibold disabled:opacity-40"
            >
              {soumettre.isPending ? 'Envoi…' : 'Soumettre la commande'}
            </button>
          </div>
        </>
      ) : (
        <div className="flex flex-wrap gap-3">
          <Link
            to="/order/new"
            className="bg-primary text-primary-foreground flex h-16 flex-1 items-center justify-center rounded-lg px-8 text-lg font-semibold"
          >
            Nouvelle commande
          </Link>
          <Link
            to="/"
            className="border-input flex h-16 items-center rounded-lg border px-6 text-lg font-medium"
          >
            Accueil
          </Link>
        </div>
      )}
    </div>
  );
}
